import React from "react"
import CelticDropCap from "design_system/application/components/celtic_drop_cap"
import { useRemarkSync } from "react-remark"
import wrapAmpersand from "design_system/utils/ampersand_wrapper"
import rehypeRaw from "rehype-raw"
import classNames from "classnames"
import RemarkDropcap from "design_system/utils/remark_dropcap"
import rehypeExternalLinks from "rehype-external-links"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"
import { twMerge } from "tailwind-merge"

type DropcapSize = "xs" | "sm" | "md" | "lg" | "xl"

interface ContentProps {
  /**
   * Markdown content to render
   */
  content?: string
  /**
   * Enable Celtic drop cap on first letter. Pass size string to customize.
   */
  dropcap?: boolean | DropcapSize
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Component to render as (div, section, article, etc.)
   */
  as?: React.ElementType
  [key: string]: any
}

/**
 * Content component that renders markdown with optional Celtic drop cap
 */
// Strip a leading YAML front-matter block (`--- ... ---`). It carries hidden context for search/embeddings
// (see Library::ContentScaffold) and must never render as an <hr> + visible `key: value` text on the page.
//
// Careful not to eat real horizontal rules: `---` is also a Markdown thematic break, so we only treat a
// block as front matter when it (a) sits at the very start of the content (no leading whitespace) and
// (b) opens with a `key:` line. A leading hr (`---` followed by a blank line or prose) has no `key:` on
// the next line, so it's left alone; hrs anywhere below the first line are never at position 0 to begin with.
const stripFrontMatter = (text: string) =>
  text.replace(/^﻿?---[ \t]*\r?\n(?=[^\n]*:)[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, "")

const Content = ({
  content = "",
  dropcap = false,
  className,
  as: Component = "div",
  ...props
}: ContentProps) => {
  const markdownContent = stripFrontMatter(content ?? "")
  const sanitize = defaultSchema

  // Needed since we mark dropcaps with a class to then render in the custom function below
  sanitize.attributes["*"].push("className")

  const reactContent = useRemarkSync(wrapAmpersand(markdownContent), {
    remarkPlugins: [[RemarkDropcap, { renderDropcap: dropcap }]],
    remarkToRehypeOptions: { allowDangerousHtml: true },
    rehypePlugins: [
      [rehypeSanitize, sanitize],
      rehypeRaw,
      //  ugc = User Generated Content
      [rehypeExternalLinks, { rel: ["ugc, noopener"], target: "_blank" }],
    ],
    rehypeReactOptions: {
      components: {
        span: (props: any) => {
          if (props.className === "dropcap" && dropcap) {
            return (
              <CelticDropCap
                className="m-0 mr-3"
                size={typeof dropcap === "string" ? dropcap : "md"}
                letter={props.children[0].toLowerCase()}
              />
            )
          }

          return <span {...props} />
        },
      },
    },
  })

  return (
    <Component
      data-testid="content"
      className={twMerge(
        "prose prose-ol:text-sm prose-ul:text-sm prose-hr:my-4 prose-blockquote:-ml-5 dark:prose-invert first-line:italic max-w-none! prose-headings:font-normal prose-img:mt-0!",
        className
      )}
      {...props}
    >
      {reactContent}
    </Component>
  )
}

export default Content
